import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { styled, alpha } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useCity } from '../contexts/CityContext';
import { usePopularCities } from '../features/locations/hooks/useLocations';
import type { CityResponse } from '@lov/shared';

const CityGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}));

const CityChip = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 16px',
  borderRadius: 10,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  cursor: 'pointer',
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  fontWeight: 500,
  textAlign: 'left',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.success.main, 0.08),
    borderColor: theme.palette.success.main,
    color: theme.palette.success.dark,
  },
}));

const CITY_MODAL_EXCLUDED_ROUTES = [
  '/terms',
  '/privacy',
  '/refund-policy',
  '/contact',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
];

export function CitySelectionModal() {
  const { selectedCity, setCity, skipCitySelection, needsCitySelection } = useCity();
  const { data: popularCities, isLoading } = usePopularCities();
  const [search, setSearch] = useState('');
  const { pathname } = useLocation();

  const isExcludedRoute = CITY_MODAL_EXCLUDED_ROUTES.some((route) => pathname.startsWith(route));
  const open = needsCitySelection && !selectedCity && !isExcludedRoute;

  const handleSelectCity = (city: CityResponse) => {
    setCity({ id: city.id, name: city.name, slug: city.slug });
  };

  const filteredCities = (popularCities || []).filter((city) =>
    city.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        // Prevent closing on backdrop click or escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 1 },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 700,
          fontSize: '1.25rem',
          pb: 0,
        }}
      >
        Select Your City
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', fontFamily: '"IBM Plex Sans", sans-serif', mb: 2 }}
        >
          Choose your city to discover venues near you.
        </Typography>

        {/* Search input */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            mb: 1,
          }}
        >
          <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cities..."
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontSize: '0.875rem',
              width: '100%',
              color: 'inherit',
            }}
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <CityGrid>
            {filteredCities.map((city) => (
              <CityChip key={city.id} type="button" onClick={() => handleSelectCity(city)}>
                {city.name}
              </CityChip>
            ))}
            {filteredCities.length === 0 && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  gridColumn: 'span 2',
                  textAlign: 'center',
                  py: 3,
                }}
              >
                No cities found
              </Typography>
            )}
          </CityGrid>
        )}

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography
            component="button"
            onClick={skipCitySelection}
            sx={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'text.disabled',
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontSize: '0.8125rem',
              '&:hover': { color: 'text.secondary' },
            }}
          >
            Skip for now
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
